import { useEffect, useRef, useState } from 'react';
import { Icon, IconButton, Stack, styled, Text, TextArea } from 'shell/components';

const ChatComposer = ({ disabled, placeholder, value, disabledTooltip, onChange, onSend }) => {
    const textAreaRef = useRef(null);
    const [draftValue, setDraftValue] = useState(value);

    useEffect(() => {
        setDraftValue((currentDraftValue) => (currentDraftValue === value ? currentDraftValue : value));
    }, [value]);

    return (
        <ComposerShell>
            {!draftValue ? (
                <ComposerPlaceholderOverlay
                    aria-hidden={true}
                    tabIndex={-1}
                    type={'button'}
                    onClick={() => textAreaRef.current?.focus()}
                >
                    <Icon kind={'gradientSparkle'} />
                    <Text color={'inherit'} variant={'p2'}>
                        {placeholder}
                    </Text>
                </ComposerPlaceholderOverlay>
            ) : null}
            <ComposerTextArea
                ref={textAreaRef}
                helperTextPosition={'bottom'}
                maxRows={6}
                minRows={4}
                value={draftValue}
                onChange={(event) => {
                    setDraftValue(event.target.value);
                    onChange(event.target.value);
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        onSend();
                    }
                }}
            />
            <SendButton
                disabled={disabled || !draftValue.trim()}
                kind={'send'}
                size={'medium'}
                variant={'square'}
                onClick={onSend}
                tooltip={disabled && disabledTooltip ? { component: disabledTooltip } : undefined}
            />
        </ComposerShell>
    );
};

const ComposerShell = styled(Stack)(({ theme }) => {
    return {
        position: 'relative',
        width: '100%',
        borderRadius: '18px',
        border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        background: theme.palette.ensightful.shades[0],
        transition: theme.transitions.default,

        '&:focus-within': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 2px`,
            outlineOffset: '2px',
        },
    };
});

const ComposerTextArea = styled(TextArea)(({ theme }) => {
    return {
        '& .MuiInputBase-root, & .MuiOutlinedInput-root, & textarea': {
            paddingRight: '3.5rem',
        },
        '& .MuiInputBase-root': {
            background: 'transparent',
            outline: 'none',
        },
        '& .MuiInputBase-root > fieldset': {
            borderColor: 'transparent',
        },
        '& .MuiInputBase-root > textarea': {
            padding: theme.spacing(3, 4),
        },
    };
});

const ComposerPlaceholderOverlay = styled('button')(({ theme }) => {
    return {
        position: 'absolute',
        top: theme.spacing(3),
        left: theme.spacing(4),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        margin: 0,
        padding: 0,
        border: 0,
        background: 'transparent',
        color: theme.palette.ensightful.neutral[500],
        cursor: 'text',
        zIndex: 1,
        pointerEvents: 'none',
    };
});

const SendButton = styled(IconButton)(({ theme }) => {
    return {
        position: 'absolute',
        right: theme.spacing(1.5),
        bottom: theme.spacing(1.5),
    };
});

export default ChatComposer;
